import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Heading, Text, Button } from 'frosted-ui';
import { ArrowLeft16, ArrowRight16 } from '@frosted-ui/icons';
import { Rise } from '../components/animation';
import RevenueGoalLine from '../components/Onboarding/RevenueGoalLine';
import { isFirstRunState, type FirstRunState } from '../components/Onboarding/firstRun';
import { useIsMobile } from '../hooks/useIsMobile';
import { PATHS, type TestPath } from '../utils/paths';

type Choice = TestPath | 'none';

function PathIcon({ path }: { path: TestPath }) {
  const common = {
    width: 36,
    height: 36,
    viewBox: '0 0 36 36',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (path === 'in_whop') {
    return (
      <svg {...common}>
        <rect x="4" y="6" width="28" height="24" rx="4" />
        <path d="M4 12h28" />
        <circle cx="13" cy="21" r="3" />
        <path d="M20 19h7M20 23h5" />
      </svg>
    );
  }
  if (path === 'link') {
    return (
      <svg {...common}>
        <path d="M15 21a5 5 0 0 0 7 0l5-5a5 5 0 0 0-7-7l-1.5 1.5" />
        <path d="M21 15a5 5 0 0 0-7 0l-5 5a5 5 0 0 0 7 7l1.5-1.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="13" r="4" />
      <circle cx="25" cy="13" r="4" />
      <path d="M5 28c1-4 4-6 7-6s6 2 7 6M18 28c1-4 4-6 7-6s6 2 7 6" />
    </svg>
  );
}

const TINT: Record<TestPath, string> = {
  in_whop: 'var(--blue-a3)',
  link: 'var(--orange-a3)',
  partner: 'var(--purple-a3)',
};

/** "Where do your buyers find you?" — the start of every new test. */
export default function PathChooser() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  // First run (step 2) is the same page; router state carries it onward.
  const firstRun = isFirstRunState(location.state);
  const nextState: FirstRunState = { firstRun };
  const [picked, setPicked] = useState<Choice>('link');

  const hint =
    picked === 'none'
      ? 'Next: a short guide to setting up a free tier.'
      : PATHS.find((p) => p.id === picked)?.next;

  function openGuide() {
    navigate('/new/no-buyers', { state: nextState });
  }

  function handleContinue() {
    if (picked === 'none') openGuide();
    else navigate(`/experiments/new?path=${picked}`, { state: nextState });
  }

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <Rise>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxWidth: 680 }}>
          <Heading size={isMobile ? '7' : '8'}>Where do your buyers find you?</Heading>
          <Text size="3" color="gray">
            Pick the place most of your sales start. We'll test your price right there, and you can
            add more links to the same test later.
          </Text>
          {firstRun && <RevenueGoalLine />}
        </div>
      </Rise>

      <div
        role="radiogroup"
        aria-label="Where buyers find you"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--space-4)' }}
      >
        {PATHS.map((p, i) => {
          const selected = picked === p.id;
          return (
            <Rise key={p.id} delay={80 + i * 60}>
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setPicked(p.id)}
                className="vara2-card-hover"
                style={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-4)',
                  padding: 'var(--space-5)',
                  borderRadius: 'var(--radius-5)',
                  backgroundColor: 'var(--color-panel-solid)',
                  border: selected ? '2px solid var(--accent-9)' : '2px solid var(--gray-a5)',
                  boxShadow: selected ? '0 6px 20px var(--accent-a4)' : 'none',
                }}
              >
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 14,
                    backgroundColor: TINT[p.id],
                    color: 'var(--gray-12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PathIcon path={p.id} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  <Heading size="5">{p.title}</Heading>
                  <Text size="2" color="gray" style={{ fontStyle: 'italic' }}>
                    “{p.quote}”
                  </Text>
                </div>
                <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                  {p.points.map((pt) => (
                    <li key={pt}>
                      <Text size="2">{pt}</Text>
                    </li>
                  ))}
                </ul>
              </button>
            </Rise>
          );
        })}
      </div>

      <Rise delay={300}>
        <div
          className="vara2-card-hover"
          style={{
            boxSizing: 'border-box',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
            padding: 'var(--space-4) var(--space-5)',
            borderRadius: 'var(--radius-4)',
            backgroundColor: 'var(--color-panel-solid)',
            border: picked === 'none' ? '2px solid var(--accent-9)' : '2px dashed var(--gray-a6)',
          }}
        >
          <button
            type="button"
            role="radio"
            aria-checked={picked === 'none'}
            onClick={() => setPicked('none')}
            style={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-1)',
              flex: '1 1 320px',
            }}
          >
            <Text size="3" weight="medium">I don't have buyers yet</Text>
            <Text size="2" color="gray">
              A price test needs people reaching your checkout. We'll help you set up a free tier
              first — it becomes the audience for your first in-whop test.
            </Text>
          </button>
          {/* Goes straight to the guide; the rest of the card only selects it. */}
          <Button size="2" variant="ghost" onClick={openGuide}>
            Show me how
            <ArrowRight16 />
          </Button>
        </div>
      </Rise>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <Text size="2" color="gray">{hint}</Text>
        <div style={{ display: 'flex', gap: 'var(--space-3)', marginLeft: 'auto' }}>
          {/* In first run, skipping lives in the step header; this goes back to step 1. */}
          {firstRun ? (
            <Button
              size="3"
              variant="ghost"
              color="gray"
              style={{ minHeight: 44 }}
              onClick={() => navigate('/welcome/result', { state: nextState })}
            >
              <ArrowLeft16 />
              Back
            </Button>
          ) : (
            <Button size="3" variant="ghost" color="gray" onClick={() => navigate('/')}>
              Cancel
            </Button>
          )}
          <Button size="3" variant="solid" style={{ minHeight: 44 }} onClick={handleContinue}>
            Continue
            <ArrowRight16 />
          </Button>
        </div>
      </div>
    </div>
  );
}
